import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { getCartItemCount } from '@theme/cart-drawer-section';

const PERSIST_KEY = 'cart-drawer-open';
const PERSIST_TTL_MS = 30_000;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {Set<string>} */
  #previousKeys = new Set();
  /** @type {{ variantId: string, quantity: number, title: string, sellingPlan?: string } | null} */
  #pendingRemove = null;
  /** @type {HTMLElement | null} */
  #activeToast = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #toastTimer = null;

  /** @type {(() => void) | null} */
  #scrollShadowHandler = null;

  connectedCallback() {
    super.connectedCallback();
    // Capture phase so we observe pre-morph cart state before cart-items-component morphs.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, true);
    this.addEventListener('click', this.#captureRemoveClick, true);
    this.addEventListener('click', this.#onSubscribeConvert);
    this.addEventListener(DialogOpenEvent.eventName, this.#pinDrawerToViewport);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpenSyncPromo);
    this.addEventListener(DialogCloseEvent.eventName, this.#unpinDrawerFromViewport);

    this.#captureCurrentKeys();
    this.#restorePersistedState();
    this.#bindItemsScrollShadow();
    this.#ensureDrawerCoversAnnouncement();
    void this.#syncFreeShippingPromoUi();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, true);
    this.removeEventListener('click', this.#captureRemoveClick, true);
    this.removeEventListener('click', this.#onSubscribeConvert);
    this.removeEventListener(DialogOpenEvent.eventName, this.#pinDrawerToViewport);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpenSyncPromo);
    this.removeEventListener(DialogCloseEvent.eventName, this.#unpinDrawerFromViewport);
    this.#unpinDrawerFromViewport();
    this.#unbindItemsScrollShadow();
  }

  /**
   * Quantity morphs replace drawer markup and drop the viewport pin.
   * Re-apply after connect and every cart:update while the dialog is open.
   */
  #ensureDrawerCoversAnnouncement = () => {
    const dialog = this.refs?.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    document.documentElement.classList.add('cart-drawer-open');
    this.#pinDrawerToViewport();
  };

  /**
   * Sticky header / announcement can trap the drawer below the red bar.
   * If the open dialog doesn't start at the viewport top, pull it up to cover.
   */
  #pinDrawerToViewport = () => {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!dialog.open) return;
        const top = Math.round(dialog.getBoundingClientRect().top);
        if (top <= 1) {
          this.#clearDrawerViewportPin(dialog);
          return;
        }

        const annVar = parseFloat(
          getComputedStyle(document.body).getPropertyValue('--announcement-height')
        );
        const offset = Number.isFinite(annVar) && annVar > 0 ? Math.round(annVar) : top;
        dialog.style.setProperty('top', '0px', 'important');
        dialog.style.setProperty('margin-top', `-${offset}px`, 'important');
        dialog.style.setProperty('height', `calc(100dvh + ${offset}px)`, 'important');
        dialog.style.setProperty('max-height', 'none', 'important');
        document.documentElement.classList.add('cart-drawer-open');
      });
    });
  };

  #unpinDrawerFromViewport = () => {
    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLDialogElement) {
      this.#clearDrawerViewportPin(dialog);
    }
    document.documentElement.classList.remove('cart-drawer-open');
  };

  /** @param {HTMLDialogElement} dialog */
  #clearDrawerViewportPin(dialog) {
    dialog.style.removeProperty('top');
    dialog.style.removeProperty('margin-top');
    dialog.style.removeProperty('height');
    dialog.style.removeProperty('max-height');
  }

  #handleCartUpdate = (/** @type {Event} */ event) => {
    // Snapshot keys BEFORE the bubble-phase morphSection replaces the cart DOM.
    const previousKeys = new Set(this.#previousKeys);

    const detail = /** @type {any} */ (event).detail || {};
    const data = detail.data || {};
    const source = data.source;

    this.classList.remove('cart-drawer--pending');

    const isAddSource =
      source === 'product-form-component' ||
      source === 'cart-drawer-add-ons' ||
      source === 'cart-drawer-undo' ||
      source === 'cart-sync';

    if (isAddSource && this.hasAttribute('auto-open') && !this.querySelector('.cart-drawer--empty')) {
      requestAnimationFrame(() => this.showDialog());
    }

    // After morph + paint, locate any newly-added row and flash it.
    if (isAddSource) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.#flashNewRow(previousKeys);
          this.#captureCurrentKeys();
          this.#recoverEmptyDrawerIfNeeded();
        })
      );
    }

    if (
      source === 'product-form-component' ||
      source === 'cart-drawer-add-ons'
    ) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.#showAddConfirmationToast())
      );
    }

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        void this.#syncFreeShippingPromoUi();
      })
    );

    if (!isAddSource) {
      requestAnimationFrame(() => this.#captureCurrentKeys());
    }

    requestAnimationFrame(() => this.#bindItemsScrollShadow());
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.#ensureDrawerCoversAnnouncement())
    );

    // Show undo toast if the user just removed a line.
    if (this.#pendingRemove) {
      const removed = this.#pendingRemove;
      this.#pendingRemove = null;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.#showUndoToast(removed))
      );
    }
  };

  #captureRemoveClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeBtn = target.closest('.cart-items__remove');
    if (!removeBtn) return;
    const row = removeBtn.closest('.cart-items__table-row');
    if (!(row instanceof HTMLElement)) return;

    const variantId = row.dataset.variantId;
    if (!variantId) return;

    this.#pendingRemove = {
      variantId,
      quantity: parseInt(row.dataset.quantity || '1', 10) || 1,
      title:
        row.querySelector('.cart-items__title')?.textContent?.trim() ||
        row.dataset.productTitle ||
        'Item',
      sellingPlan: row.dataset.sellingPlan || undefined,
    };
  };

  #recoverEmptyDrawerIfNeeded() {
    const dialog = this.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLElement) || !dialog.classList.contains('cart-drawer--empty')) {
      return;
    }

    const bubbleText = document.querySelector('.header-actions__cart-icon .cart-bubble__text')?.textContent || '';
    const countMatch = bubbleText.match(/\d+/);
    const itemCount = countMatch ? parseInt(countMatch[0], 10) : 0;

    if (itemCount > 0) {
      sectionRenderer.renderSection('cart-drawer', { cache: false, replace: true });
    }
  }

  /** Convert one-time cart lines to the product’s first selling plan. */
  #onSubscribeConvert = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('[data-cart-subscribe-convert]');
    if (!(btn instanceof HTMLButtonElement)) return;
    event.preventDefault();
    if (btn.disabled) return;

    const rows = Array.from(
      this.querySelectorAll('.cart-items__table-row[data-key][data-subscribe-plan-id]')
    ).filter((row) => !row.getAttribute('data-selling-plan'));
    if (!rows.length) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const changeUrl =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_change_url) || '/cart/change.js';

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      let lastPayload = null;
      for (const row of rows) {
        const key = row.getAttribute('data-key');
        const planId = Number(row.getAttribute('data-subscribe-plan-id'));
        const qty = Number(row.getAttribute('data-quantity')) || 1;
        if (!key || !planId) continue;
        const res = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: qty,
            selling_plan: planId,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        lastPayload = await res.json();
        if (!res.ok || lastPayload.errors) {
          throw new Error(lastPayload.description || lastPayload.errors || 'Could not subscribe');
        }
      }
      if (lastPayload) {
        document.dispatchEvent(
          new CartAddEvent(lastPayload, 'cart-drawer-subscribe', {
            source: 'cart-drawer-add-ons',
            itemCount: lastPayload.item_count ?? 0,
            variantId: '',
            sections: lastPayload.sections,
          })
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  };

  #captureCurrentKeys() {
    this.#previousKeys = new Set(
      Array.from(this.querySelectorAll('.cart-items__table-row[data-key]'))
        .map((r) => r.getAttribute('data-key'))
        .filter((key) => typeof key === 'string' && key.length > 0)
    );
  }

  #flashNewRow(previousKeys) {
    const rows = this.querySelectorAll('.cart-items__table-row[data-key]');
    /** @type {HTMLElement | null} */
    let newRow = null;
    for (const row of rows) {
      const key = row.getAttribute('data-key');
      if (key && !previousKeys.has(key) && row instanceof HTMLElement) {
        newRow = row;
        break;
      }
    }
    if (!newRow) return;
    newRow.classList.add('cart-items__table-row--just-added');
    try {
      newRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (_) {
      newRow.scrollIntoView();
    }
    const price = newRow.querySelector('text-component');
    if (price instanceof HTMLElement) {
      price.classList.add('is-flashing');
      setTimeout(() => price.classList.remove('is-flashing'), 700);
    }
    setTimeout(() => newRow.classList.remove('cart-items__table-row--just-added'), 1300);
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatCents(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, Shopify.money_format);
    }
    return `$${Math.round(cents / 100)}`;
  }

  async #showAddConfirmationToast() {
    if (this.#pendingRemove) return;
    if (this.dataset.freeShippingPromo === 'true') return;

    const threshold = parseInt(this.dataset.freeShippingThreshold || '0', 10);
    if (!threshold) return;

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      if (this.#cartHasFreeShippingPromo(cart)) {
        await this.#syncFreeShippingPromoUi(cart);
        return;
      }
      const total = cart.total_price || 0;
      /** @type {string | undefined} */
      let message;

      if (total >= threshold) {
        message = 'Free shipping unlocked';
      } else {
        const soberLines = (cart.items || []).filter((/** @type {any} */ item) => {
          const url = item.url || '';
          const title = item.product_title || item.title || '';
          return (
            url.includes('the-sober-july-24-pack') || /sober.*24-pack/i.test(title)
          );
        });
        const soberQty = soberLines.reduce(
          (sum, /** @type {any} */ item) => sum + (item.quantity || 0),
          0
        );

        if (soberQty === 1) {
          const unit = soberLines[0]?.price || 0;
          if (unit > 0 && total + unit >= threshold) {
            message = `+${this.#formatCents(unit)} for free shipping — add another box`;
          }
        }

        if (!message) {
          const left = threshold - total;
          if (left > 0) {
            message = `Add ${this.#formatCents(left)} more for free shipping`;
          }
        }
      }

      if (message) this.#showStatusToast(message);
    } catch (_) {
      // ignore
    }
  }

  /**
   * Free-shipping codes (e.g. FIRSTBOX) live on cart.discount_codes in Ajax cart.
   * Liquid often cannot see shipping discounts until checkout — JS is the safety net.
   * @param {any} cart
   * @returns {boolean}
   */
  #cartHasFreeShippingPromo(cart) {
    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    if (
      codes.some((entry) => {
        if (!entry || entry.applicable === false) return false;
        const code = String(entry.code || '').toUpperCase();
        return code === 'FIRSTBOX' || code.includes('FIRSTBOX');
      })
    ) {
      return true;
    }

    const apps = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];
    return apps.some((app) => {
      const title = String(app?.title || '').toUpperCase();
      return title === 'FIRSTBOX' || title.includes('FIRSTBOX');
    });
  }

  #onDialogOpenSyncPromo = () => {
    void this.#syncFreeShippingPromoUi();
  };

  /**
   * When FIRSTBOX (or another free-ship code) is on the cart, hide "$X more for
   * free shipping" / "calculated at checkout" messaging that contradicts the promo.
   * @param {any} [cart]
   */
  async #syncFreeShippingPromoUi(cart) {
    try {
      const cartData = cart || (await fetch('/cart.js').then((r) => r.json()));
      if (!this.#cartHasFreeShippingPromo(cartData)) return;

      this.dataset.freeShippingPromo = 'true';
      this.removeAttribute('data-free-shipping-threshold');

      this.querySelectorAll('.cart-free-shipping--drawer').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (el.classList.contains('cart-free-shipping--promo')) {
          el.hidden = false;
          return;
        }
        el.hidden = true;
      });

      let promo = this.querySelector('.cart-free-shipping--promo');
      if (!promo) {
        const body = this.querySelector('.cart-drawer__body');
        if (body) {
          promo = document.createElement('div');
          promo.className =
            'cart-free-shipping cart-free-shipping--drawer cart-free-shipping--promo';
          promo.setAttribute('role', 'status');
          promo.innerHTML =
            '<p class="cart-free-shipping__line cart-free-shipping__line--success cart-free-shipping__line--combined"><span class="success-pulse">Free shipping applied</span></p>';
          body.appendChild(promo);
        }
      }

      const shipCalc = this.querySelector('.cart-drawer-summary__shipping-calc');
      if (shipCalc instanceof HTMLElement) {
        shipCalc.textContent = 'Free';
        shipCalc.classList.remove('cart-drawer-summary__shipping-calc');
        shipCalc.classList.add('cart-drawer-summary__shipping-free');
      }

      const meta = this.querySelector('.cart-drawer-summary__meta');
      if (meta instanceof HTMLElement) {
        meta.textContent = 'Taxes included. Free shipping on this order.';
      }
    } catch (_) {
      // ignore
    }
  }

  /**
   * @param {string} message
   */
  #showStatusToast(message) {
    this.#dismissActiveToast(true);

    const toast = document.createElement('div');
    toast.className = 'cart-drawer-toast cart-drawer-toast--success';
    toast.setAttribute('role', 'status');

    const text = document.createElement('span');
    text.className = 'cart-drawer-toast__text';
    text.textContent = message;
    toast.appendChild(text);

    document.body.appendChild(toast);
    this.#activeToast = toast;
    this.#toastTimer = setTimeout(() => this.#dismissActiveToast(), 4500);
  }

  #showUndoToast({ variantId, quantity, title, sellingPlan }) {
    this.#dismissActiveToast(true);

    const toast = document.createElement('div');
    toast.className = 'cart-drawer-toast';
    toast.setAttribute('role', 'status');

    const text = document.createElement('span');
    text.className = 'cart-drawer-toast__text';
    const cleanTitle = title.length > 36 ? `${title.slice(0, 33)}…` : title;
    text.textContent = `Removed ${cleanTitle}`;
    toast.appendChild(text);

    const undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'cart-drawer-toast__undo';
    undo.textContent = 'Undo';
    undo.addEventListener('click', () =>
      this.#restoreLine({ variantId, quantity, sellingPlan }, toast)
    );
    toast.appendChild(undo);

    document.body.appendChild(toast);
    this.#activeToast = toast;
    this.#toastTimer = setTimeout(() => this.#dismissActiveToast(), 6000);
  }

  #dismissActiveToast(immediate = false) {
    if (this.#toastTimer) {
      clearTimeout(this.#toastTimer);
      this.#toastTimer = null;
    }
    const toast = this.#activeToast;
    if (!toast) return;
    this.#activeToast = null;
    if (immediate) {
      toast.remove();
      return;
    }
    toast.classList.add('cart-drawer-toast--leaving');
    setTimeout(() => toast.remove(), 220);
  }

  async #restoreLine({ variantId, quantity, sellingPlan }, toast) {
    if (!variantId) return;
    const undoBtn = toast.querySelector('.cart-drawer-toast__undo');
    if (undoBtn instanceof HTMLButtonElement) {
      undoBtn.disabled = true;
      undoBtn.textContent = 'Restoring…';
    }

    try {
      /** @type {Record<string, unknown>} */
      const body = {
        id: Number(variantId),
        quantity: Number(quantity) || 1,
        sections: 'cart-drawer',
        sections_url: window.location.pathname,
      };
      if (sellingPlan) body.selling_plan = Number(sellingPlan);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Restore failed: ${response.status}`);
      const payload = await response.json();

      // The bubble is set (not incremented) for this source, so it needs the
      // absolute count. /cart/add.js returns the line item, not the cart, so
      // read the new total out of the section markup we just asked for.
      const restoredSection = payload.sections?.['cart-drawer'];
      const restoredCount = restoredSection
        ? parseInt(
            new DOMParser()
              .parseFromString(restoredSection, 'text/html')
              .querySelector('[ref="cartItemCount"]')?.textContent ?? '',
            10
          )
        : NaN;

      document.dispatchEvent(
        new CartAddEvent(payload, 'cart-drawer', {
          source: 'cart-drawer-undo',
          itemCount: Number.isFinite(restoredCount) ? restoredCount : getCartItemCount() + (Number(quantity) || 1),
          variantId: String(variantId),
          sections: payload.sections,
        })
      );

      if (!payload.sections || !payload.sections['cart-drawer']) {
        sectionRenderer.renderSection('cart-drawer', { cache: false });
      }
    } catch (error) {
      console.error('Cart undo failed', error);
    } finally {
      this.#dismissActiveToast();
    }
  }

  #resetDrawerScroll() {
    const items = this.querySelector('.cart-drawer__items');
    if (items instanceof HTMLElement) {
      items.scrollTop = 0;
    }
    this.#updateSummaryScrollShadow();
  }

  #updateSummaryScrollShadow() {
    const items = this.querySelector('.cart-drawer__items');
    const summary = this.querySelector('.cart-drawer__summary');
    if (!(items instanceof HTMLElement) || !(summary instanceof HTMLElement)) return;
    summary.classList.toggle('cart-drawer__summary--scrolled', items.scrollTop > 6);
  }

  #bindItemsScrollShadow() {
    this.#unbindItemsScrollShadow();
    const items = this.querySelector('.cart-drawer__items');
    if (!(items instanceof HTMLElement)) return;

    this.#scrollShadowHandler = () => this.#updateSummaryScrollShadow();
    items.addEventListener('scroll', this.#scrollShadowHandler, { passive: true });
    this.#updateSummaryScrollShadow();
  }

  #unbindItemsScrollShadow() {
    const items = this.querySelector('.cart-drawer__items');
    if (items instanceof HTMLElement && this.#scrollShadowHandler) {
      items.removeEventListener('scroll', this.#scrollShadowHandler);
    }
    this.#scrollShadowHandler = null;
  }

  #restorePersistedState() {
    if (this.dataset.persistState !== 'true') return;
    try {
      const raw = sessionStorage.getItem(PERSIST_KEY);
      if (!raw) return;
      const { open, timestamp } = JSON.parse(raw);
      if (!open) return;
      if (Date.now() - timestamp > PERSIST_TTL_MS) {
        sessionStorage.removeItem(PERSIST_KEY);
        return;
      }
      requestAnimationFrame(() => this.showDialog());
    } catch (_) {
      // ignore
    }
  }

  #persistState(open) {
    if (this.dataset.persistState !== 'true') return;
    try {
      if (open) {
        sessionStorage.setItem(
          PERSIST_KEY,
          JSON.stringify({ open: true, timestamp: Date.now() })
        );
      } else {
        sessionStorage.removeItem(PERSIST_KEY);
      }
    } catch (_) {
      // ignore
    }
  }

  open() {
    this.showDialog();
    this.#resetDrawerScroll();
    this.#bindItemsScrollShadow();
    this.#persistState(true);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    this.#dismissActiveToast();
    this.#persistState(false);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
